import { create } from "zustand"

export type Modal =
  | "login"
  | "signup"
  | "search"
  | "fs-mobile"
  | "review"
  | "property"
  | "product-category"
  | "delete"
  | "alert"

export type AlertAction = "delete" | "publish" | "unpublish" | "warning" | "activate"

export interface AlertModalData {
  action: AlertAction
  title: string
  description: string
  itemTitle?: string
  onConfirm: () => void | Promise<void> | Promise<unknown>
  confirmButtonText?: string
  cancelButtonText?: string 
}

interface ModalStore {
  modals: Partial<Record<Modal, boolean>>
  newlyCreatedCategoryId: string | null
  setNewlyCreatedCategoryId: (id: string | null) => void
  modalData: Partial<Record<Modal, { contentType?: string; contentId?: string }>>
  alertData: AlertModalData | null
  openModal: (modal: Modal, data?: { contentType?: string; contentId?: string }) => void
  openAlert: (data: AlertModalData) => void
  closeModal: (modal: Modal) => void
  closeAllModals: () => void
}

export const useModalStore = create<ModalStore>((set) => ({
  modals: {
    login: false,
    signup: false,
    search: false,
    "fs-mobile": false,
    review: false,
    property: false,
    "product-category": false,
    delete: false,
    alert: false,
  },

  // Initialize modalData with default values (empty objects)
  modalData: {
    delete: { contentType: undefined, contentId: undefined },
  },

  alertData: null,

  newlyCreatedCategoryId: null,

  openModal: (modalId, data) =>
    set((state) => ({
      modals: { ...state.modals, [modalId]: true },
      modalData: {
        ...state.modalData,
        [modalId]: { ...state.modalData[modalId], ...data },
      },
    })),

  openAlert: (data) =>
    set({
      modals: { alert: true },
      alertData: data,
    }),

  closeModal: (modalId) =>
    set((state) => ({
      modals: { ...state.modals, [modalId]: false },
      modalData: {
        ...state.modalData,
        [modalId]: { contentType: undefined, contentId: undefined },
      },
      ...(modalId === "alert" && { alertData: null }),
    })),

  closeAllModals: () =>
    set((state) => ({
      modals: Object.keys(state.modals).reduce<Partial<Record<Modal, boolean>>>((acc, modalId) => {
        acc[modalId as Modal] = false
        return acc
      }, {}),
      modalData: Object.keys(state.modalData).reduce<
        Partial<Record<Modal, { contentType?: string; contentId?: string }>>
      >((acc, modalId) => {
        acc[modalId as Modal] = { contentType: undefined, contentId: undefined }
        return acc
      }, {}),
      alertData: null,
    })),

  setNewlyCreatedCategoryId: (id: string | null) => set({ newlyCreatedCategoryId: id }),
}))
